import { z } from "zod";

const toText = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  return value;
};

const requiredText = (label) =>
  z.preprocess(
    toText,
    z
      .string({ required_error: `${label} is required` })
      .trim()
      .min(1, `${label} is required`)
  );

const optionalText = z.preprocess(
  (value) => {
    if (value == null || value === "") return undefined;
    return value;
  },
  z.string().optional()
);

const apdDeductibleOptions = ["$1,000", "$1,500", "$2,000", "$2,500"];

const VehicleSchema = z
  .object({
    vin: requiredText("VIN"),
    year: requiredText("Year"),
    make: requiredText("Make"),
    model: requiredText("Model"),
    garagingZip: requiredText("Garaging zip"),
    ownershipYears: optionalText,
    ownershipMonths: optionalText,
    primaryUse: requiredText("Primary use"),
    primaryUseOther: optionalText,
    rideShare: z.boolean().default(false),
    delivery: z.boolean().default(false),
    annualMiles: requiredText("Annual miles"),
    lienholder: z.boolean().default(false),
    lienholderName: optionalText,
    lienholderAddress: optionalText,
    lienholderAddress2: optionalText,
    lienholderCity: optionalText,
    lienholderState: optionalText,
    lienholderZip: optionalText,
    apdCoverage: z.boolean().default(false),
    apdDeductible: optionalText,
  })
  .superRefine((data, ctx) => {
    const hasOwnership =
      String(data.ownershipYears || "").trim() ||
      String(data.ownershipMonths || "").trim();
    if (!hasOwnership) {
      ctx.addIssue({
        code: "custom",
        path: ["ownershipYears"],
        message: "Ownership duration is required",
      });
    }

    if (data.primaryUse === "Other" && !String(data.primaryUseOther || "").trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["primaryUseOther"],
        message: "Please specify the primary use",
      });
    }

    if (data.lienholder) {
      const checks = [
        ["lienholderName", "Lienholder name is required"],
        ["lienholderAddress", "Lienholder address is required"],
        ["lienholderCity", "Lienholder city is required"],
        ["lienholderState", "Lienholder state is required"],
        ["lienholderZip", "Lienholder zip is required"],
      ];

      checks.forEach(([field, message]) => {
        if (!String(data[field] || "").trim()) {
          ctx.addIssue({
            code: "custom",
            path: [field],
            message,
          });
        }
      });
    }

    if (data.apdCoverage) {
      if (!String(data.apdDeductible || "").trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["apdDeductible"],
          message: "APD deductible is required",
        });
      } else if (!apdDeductibleOptions.includes(data.apdDeductible)) {
        ctx.addIssue({
          code: "custom",
          path: ["apdDeductible"],
          message: "APD deductible must be a valid option",
        });
      }
    }
  });

export const Step3Schema = z.object({
  vehicles: z
    .array(VehicleSchema)
    .min(1, "Please add at least one vehicle")
    .max(6, "Maximum of 6 vehicles"),
});
